import type { AssetStore } from '../assets/asset-store';
import { backgroundStore } from '../assets/background-store';
import type { WorldSetupState } from '../game/world';
import type { Camera } from './camera';
import { NebulaField } from './nebulae';
import { PHASE, profiler } from '../debug/profiler';

const NEAR_DIVISOR = 8;
const FAR_DIVISOR = 20;
const FAR_SCALE = 0.73;
const FAR_ALPHA = 0.5;
const FADE_MS = 700;

export class Starfield {
  private startedAt: number | null = null;
  private fadeValue = 0;
  private readonly nebulae = new NebulaField();

  constructor(private readonly assets: AssetStore) {}

  get settled(): boolean {
    return this.fadeValue >= 1 && !this.nebulae.busy;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    world: WorldSetupState,
    now: number,
    nebulae = true,
  ): void {
    const pattern = this.assets.backgroundPattern(ctx);
    if (pattern === null) {
      this.startedAt = null;
      this.fadeValue = 0;
      return;
    }
    this.startedAt ??= now;
    const fade = Math.min(1, (now - this.startedAt) / FADE_MS);
    this.fadeValue = fade;

    const prof = profiler.enabled ? profiler : null;
    const v3 = backgroundStore.style === 'v3';
    if (v3) {
      prof?.begin(PHASE.starfieldFar);
      this.drawLayer(ctx, pattern, camera, FAR_DIVISOR, FAR_SCALE, FAR_ALPHA * fade);
      prof?.end(PHASE.starfieldFar);
    }
    prof?.begin(PHASE.starfieldNear);
    this.drawLayer(ctx, pattern, camera, NEAR_DIVISOR, 1, fade);
    prof?.end(PHASE.starfieldNear);
    if (v3 && nebulae) {
      prof?.begin(PHASE.nebulae);
      this.nebulae.draw(ctx, camera, world, fade, now);
      prof?.end(PHASE.nebulae);
    }
  }

  private drawLayer(
    ctx: CanvasRenderingContext2D,
    pattern: CanvasPattern,
    camera: Camera,
    divisor: number,
    scale: number,
    alpha: number,
  ): void {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(camera.offsetX * (1 - 1 / divisor), camera.offsetY * (1 - 1 / divisor));
    ctx.scale(scale, scale);
    ctx.fillStyle = pattern;
    ctx.fillRect(
      camera.offsetX / (divisor * scale),
      camera.offsetY / (divisor * scale),
      camera.worldWidth / scale,
      camera.worldHeight / scale,
    );
    ctx.restore();
  }
}
